import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ApiEndpoints } from '../../config/api-endpoints';
import { environment } from '../../environments/environment';
import { SmartUploadPolicyInfo } from '../shared/models/smart-upload-policy-info';
import { ActivePolicy, ShipmentFileModel, ShipmentFileParameters, ShipmentFileSummaryParameters, ShipmentList, SmartUploadShipmentFile } from '../shared/models/smart-upload-shipment-file';
import { request } from 'https';
import { delay, map } from 'rxjs/operators';
import { SmartUploadSubmittedList } from '../shared/models/smart-upload-submitted-list';

@Injectable({
    providedIn: 'root'
})
export class SmartUploadService {

    private baseUrl = environment.baseUrl.api;

    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    };

    private baseHttpOptions = {
        headers: new HttpHeaders()
    };

    constructor(
        private http: HttpClient
    ) { }

    public uploadFile(
        formData: FormData,
        policyNumber: string,
        userName: string,
        country: string,
        policyId: string,
        masterShipperNumber: string,
        internalUser: number
    ): Observable<any> {

        const options = {
            ...this.baseHttpOptions,
            params: new HttpParams()
                .set('policyNumber', policyNumber)
                .set('userName', userName)
                .set('country', country)
                .set('policyId', policyId)
                .set('masterShipperNumber', masterShipperNumber)
                .set('internalUser', internalUser)
        };

        return this.http.post<any>(
            `${this.baseUrl}${ApiEndpoints.SmrtUpload_UploadShipmentFiles}`,
            formData,
            options
        );
    }

    getSmartUploadPolicyInfo(activePolicy: ActivePolicy): Observable<SmartUploadPolicyInfo | null> {
        return this.http.post(`${this.baseUrl}${ApiEndpoints.SmrtUpload_PolicyInfo}`, activePolicy, {
            ...this.httpOptions,
            responseType: 'text'
        }).pipe(
            map(response => {
                const body = response.trim();
                if (!body || body === 'No active policy found.') {
                    return null;
                }

                const policyInfo = JSON.parse(body);
                return policyInfo === 'No active policy found.' ? null : policyInfo;
            })
        );
    }

    getshipmentFileIds(shimpentFileParameters: ShipmentFileParameters): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentFileIds}`, shimpentFileParameters, this.httpOptions);
    }

    getshipmentFileListByIds(summaryParamters: ShipmentFileSummaryParameters): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentFileSummary}`, summaryParamters, this.httpOptions);
    }

    downloadTemplate(countryCode: string) {
        return this.http.get(
            `${this.baseUrl}${ApiEndpoints.SmrtUpload_DownloadTemplate}?country=${countryCode}`,
            { responseType: 'blob' }
        );
    }



    getSmrtUpload_ImportedFileHistory(shimpentFileParameters: ShipmentFileParameters): Observable<ShipmentFileModel> {
        return this.http.post<ShipmentFileModel>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ImportedFileHistory}`, shimpentFileParameters, this.httpOptions);
    }

    getShipments(shipmentParams: any): Observable<ShipmentList[]> {
        return this.http.post<ShipmentList[]>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentList}`, shipmentParams, this.httpOptions);
    }

    getSubmittedList(shipmentParams: any): Observable<SmartUploadSubmittedList[]> {
        return this.http.post<SmartUploadSubmittedList[]>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_SubmittedList}`, shipmentParams, this.httpOptions);
    }

    reValidateColumns(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ReValidateColumns}`, payload, this.httpOptions);
    }

    getShipmentTypes(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentTypes}`, payload, this.httpOptions);
    }

    getCountries(): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_Countries}`, this.httpOptions);
    }

    shipmentEdit(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentEdit}`, payload, this.httpOptions);
    }

    shipmentDelete(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentDelete}`, payload, this.httpOptions);
    }

    shipmentSubmit(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentSubmit}`, payload, this.httpOptions);
    }

    shipmentUpdateStatus(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentStatusUpdate}`, payload, this.httpOptions);
    }

    public cancelShipment(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentCancel}`, payload, this.httpOptions);
    }

    getStates(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_States}`, payload, this.httpOptions);
    }

    export2Excel(payload: any): Observable<Blob> {
        return this.http.post(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ExportToExcel}`, payload, { 
            ...this.httpOptions,
            responseType: 'blob' 
        }) as Observable<Blob>;
    }

    getShipmentListIds(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentListIdsForRemark}`, payload, this.httpOptions);
    }

    getShipmentListByIds(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentListByIds}`, payload, this.httpOptions);
    }
    
    getServiceLevelData(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ServiceLevelData}`, payload, this.httpOptions);
    }

     getShipmentListMinInfo(payload: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}${ApiEndpoints.SmrtUpload_ShipmentListMinInfo}`, payload, this.httpOptions);
    }
}
